import { SiteUrls } from '@tableau/shared-urls';
import { DataGrid } from '@tableau/widgets-datagrid';
import * as _ from 'underscore';

import { Messages } from '../../codegen/Localize';
import { CommonDataGridColumns } from '../../common/react/CommonDataGridColumns';
import { DataGridCells } from '../../common/react/components/dataGrid/DataGridCells';
import { SortableHeader } from '../../common/react/components/dataGrid/SortableHeader';
import { FlowDataGridCells } from '../../flow/FlowDataGridDefinitions';
import { FlowBaseInfo } from '../../ts/models/FlowInfo';
import { FlowOutputStepInfo } from '../../ts/models/FlowOutputStepInfo';
import { FlowOutputStepRunListItemInfo } from '../../ts/models/FlowOutputStepRunInfo';
import { RunFlowTaskInfo } from '../../ts/models/RunFlowTaskInfo';
import { Set } from '../../ts/models/Set';
import { FlowAllowedActions } from '../../ts/ServerApi/types/FlowAllowedActions';
import { RunFlowAction } from '../../ts/services/RunFlowAction';
import { RunFlowOnOutputStepsAction } from '../../ts/services/RunFlowOnOutputStepsAction';
import { appStore } from '../AppStore';
import { SessionServerSelectors } from '../selectors/SessionInfoSelectors';
import { ServiceLocator } from '../ServiceLocator';

export type RunOutputParameters = {
  readonly flowInfo: FlowBaseInfo;
  readonly outputId: string;
};

export type FlowOverviewDataGridRow = {
  readonly runOutput: RunOutputParameters;
  readonly flowOutputStep: FlowOutputStepInfo;
  readonly outputStepMostRecentRun: FlowOutputStepRunListItemInfo;
  readonly schedules: ReadonlyArray<RunFlowTaskInfo>;
};

export module FlowOverviewGrid {
  export function columns(
    flowInfo: FlowBaseInfo,
    flowAllowedActions: FlowAllowedActions,
    isServerOrSiteAdmin: boolean,
    siteUrls: SiteUrls,
    updateData: () => void
  ) : DataGrid.Column[] {
    const runFlowAction: RunFlowAction = ServiceLocator.get('RunFlowAction');
    const runFlowOnOutputStepsAction: RunFlowOnOutputStepsAction = ServiceLocator.get('RunFlowOnOutputStepsAction');
    const flowFailureThresholdForRunPrevention = SessionServerSelectors.flowFailureThresholdForRunPrevention(appStore.getState());
    const context = ServiceLocator.getAngularActionContext();
    const runSingleOutput = (row: DataGrid.Row) => {
      runFlowOnOutputStepsAction.showRunFlowOnOutputStepsDialog(row.data.runOutput.flowInfo, [row.data.runOutput.outputId])
        .then(() => updateData());
    };
    const runAllOutputs = () => {
      runFlowAction.showRunFlowDialog(Set.singletonForIdItem(flowInfo), context)
        .then(() => updateData());
    };

    const runOutputColumn: DataGrid.Column = {
      name: 'run-output-col',
      dataKey: 'runOutput',
      sortable: false,
      resizable: false,
      preferredWidth: 130,
      headerText: Messages.flowOverviewRunAll(),
      headerFormatter: DataGridCells.IconHeader(runAllOutputs, 'tb-prep-flow-overview-run-now-icon'),
      cellFormatter: DataGridCells.IconCell({
        onIconClicked: runSingleOutput,
        iconClass: 'tb-prep-flow-overview-run-now-icon',
        title: Messages.runNow(),
        label: Messages.flowOverviewRun()
      })
    };

    const outputStepColumn: DataGrid.Column = CommonDataGridColumns.withExtraSpace(
      {
        name: 'output-step-col',
        dataKey: 'flowOutputStep',
        sortable: false,
        headerText: Messages.flowRunHistoryColumn_output(),
        direction: 'desc',
        headerFormatter: SortableHeader.Formatter(),
        cellFormatter: FlowDataGridCells.FlowOverviewOutputStepCell()
      },
    );

    const outputNameColumn: DataGrid.Column = {
      name: 'output-name-col',
      dataKey: 'outputName',
      sortable: false,
      preferredWidth: 300,
      headerText: Messages.flowOverviewOutputName(),
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: FlowDataGridCells.FlowOverviewOutputNameCell(siteUrls)
    };

    const statusColumn: DataGrid.Column = {
      name: 'status-col',
      dataKey: 'status',
      sortable: false,
      headerText: Messages.status(),
      direction: 'desc',
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: FlowDataGridCells.FlowOverviewStatusCell(siteUrls)
    };

    const scheduleColumn: DataGrid.Column = {
      name: 'schedule-col',
      dataKey: 'schedules',
      sortable: false,
      headerText: Messages.flowRunHistoryColumn_schedule(),
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: FlowDataGridCells.FlowOverviewScheduleCell(siteUrls, flowAllowedActions.runFlow, isServerOrSiteAdmin, flowFailureThresholdForRunPrevention, updateData)
    };


    const errorColumn: DataGrid.Column = {
      name: 'error-col',
      dataKey: 'errors',
      sortable: false,
      headerText: Messages.flowOverviewErrors(),
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: FlowDataGridCells.FlowOverviewErrorsCell(siteUrls, flowAllowedActions.download)
    };

    return _.compact([
      flowAllowedActions.runFlow ? runOutputColumn : null,
      outputStepColumn,
      outputNameColumn,
      statusColumn,
      scheduleColumn,
      errorColumn
    ]);
  }

}
